/**
 * Tandem — consultant insights, from the weekly visit summary, the consultant's
 * own visits and their owed work in the pool. A figure that cannot be computed
 * says so rather than showing a zero.
 *
 * The window is wider than the schedule's: 12 weeks back for delivered work,
 * enough for a trend to mean something, and 6 weeks forward for what is booked.
 */
import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Meter, Pill, SfLink } from "./ui";
import { initials, utilColour } from "./ConsultantsScreen";
import { DayCell, MonthCell } from "./ScheduleScreen";
import TimesheetEntries from "./TimesheetEntries";
import VisitModal from "./VisitModal";
import DayPlannerModal from "./DayPlannerModal";
import type { PlannerTarget } from "./DayPlannerModal";
import { useConsultantVisits, useVisitPool, useActiveConsultants, useSalesforceLink, dayCellsFor, sameDay, toISODate, addDays, mondayOf, daysUsage, dayExceptionFor, useTimesheets } from "./data";
import type { VisitConsultantT, SfVisit, DayException } from "./data";
import {
    periodPoints,
    scoreVisit,
    classify,
    VISIT_TYPE_LABEL,
    fmtPoints,
    availablePoints,
    fieldDaysPerMonth,
} from "./points";
import { usePointsConfig } from "./pointsConfig";
import { monthlyVisitTarget } from "./points";

const HISTORY_WEEKS = 12;
const FORWARD_WEEKS = 6;

const statusColour = (status?: string): [string, string] => {
    switch ((status || "").toLowerCase()) {
        case "completed":
            return [T.greenTint, T.green];
        case "booked":
            return [T.purpleTint, T.purpleDeep];
        case "closed":
            return ["#F1F3F9", T.muted];
        case "automation proposal":
            return [T.amberTint, T.amber];
        default:
            return ["#F1F3F9", T.muted];
    }
};

const fmtWhen = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return (
        d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }) +
        " · " +
        d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
};

const fmtMins = (m?: number | null) => {
    if (m == null || !isFinite(m) || m <= 0) return "—";
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${String(Math.round(m % 60)).padStart(2, "0")}m` : `${Math.round(m)}m`;
};

const median = (xs: number[]) => {
    if (xs.length === 0) return null;
    const s = [...xs].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
};

/**
 * `fill` makes the card take the full height of its grid row, so a row of cards
 * with unequal content still has a straight bottom edge. Without it the cards
 * end wherever their content does, which reads as a layout fault rather than a
 * difference in the data.
 */
const Card = ({
    title,
    sub,
    children,
    fill,
}: {
    title: string;
    sub?: string;
    children: React.ReactNode;
    fill?: boolean;
}) => (
    <Box
        sx={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: "12px",
            padding: "16px 18px",
            ...(fill ? { height: "100%", display: "flex", flexDirection: "column" } : null),
        }}
    >
        <Typography sx={{ fontSize: "14px", fontWeight: 800, color: T.ink2 }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: "11.5px", color: T.muted, mt: "1px", mb: "10px" }}>{sub}</Typography>}
        {!sub && <Box sx={{ height: "10px" }} />}
        {children}
    </Box>
);

const Stat = ({ value, label, tip, color }: { value: string | number; label: string; tip?: string; color?: string }) => (
    <Box title={tip} sx={{ background: T.rowTint, border: `1px solid ${T.borderSoft}`, borderRadius: "8px", padding: "10px 11px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: color || T.ink2, fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: "10.5px", color: T.muted, lineHeight: 1.3 }}>{label}</Typography>
    </Box>
);

/** Horizontal bar with a label and a count. */
const BarRow = ({ label, value, max, color, suffix }: { label: string; value: number; max: number; color?: string; suffix?: string }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 0" }}>
        <Typography sx={{ fontSize: "11.5px", color: T.muted, width: "128px", flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
        </Typography>
        <Box sx={{ flexGrow: 1, height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
            <Box sx={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, height: "100%", background: color || T.purple }} />
        </Box>
        <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: T.ink2, width: "46px", textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
            {value}
            {suffix || ""}
        </Typography>
    </Box>
);

/** Weekly columns over delivered history. Plots points or visits, same shape. */
const WeekTrend = ({
    weeks,
    target,
    unit,
}: {
    weeks: Array<{ label: string; pts: number; visits: number }>;
    target: number;
    unit: "points" | "visits";
}) => {
    const valueOf = (w: { pts: number; visits: number }) => (unit === "points" ? w.pts : w.visits);
    const fmt = (v: number) => (unit === "points" ? fmtPoints(v) : String(v));
    const max = Math.max(target, ...weeks.map(valueOf), 1);
    return (
        // Grows into whatever height the card has, so the bars use the space
        // rather than leaving a gap beneath them.
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4px", flexGrow: 1, minHeight: "96px", position: "relative" }}>
                {/* the weekly share of the monthly target */}
                <Box
                    title={`${fmt(target)} = a week's share of the monthly target`}
                    sx={{ position: "absolute", left: 0, right: 0, bottom: `${(target / max) * 100}%`, borderTop: `1px dashed ${T.amber}`, zIndex: 1 }}
                />
                {weeks.map((w) => (
                    <Box key={w.label} title={`w/c ${w.label} · ${fmt(valueOf(w))} ${unit}`} sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                        <Box
                            sx={{
                                height: `${Math.max(2, (valueOf(w) / max) * 100)}%`,
                                background: valueOf(w) >= target ? T.green : T.purple,
                                borderRadius: "3px 3px 0 0",
                                minHeight: "2px",
                            }}
                        />
                    </Box>
                ))}
            </Box>
            <Box sx={{ display: "flex", gap: "4px", mt: "4px" }}>
                {weeks.map((w, i) => (
                    <Typography key={w.label} sx={{ flex: 1, fontSize: "8.5px", color: T.muted2, textAlign: "center" }}>
                        {i % 2 === 0 ? w.label : ""}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

const ConsultantDetail = ({
    consultant,
    teamName,
    weekMonday,
    dates,
    mode,
    periodLabel,
    onJumpToDate,
    onGoPool,
}: {
    consultant: VisitConsultantT;
    teamName: string;
    weekMonday: Date;
    /** The weekdays of the period selected in the top bar. */
    dates: Date[];
    mode: "week" | "month";
    periodLabel: string;
    /** Month cell → jump into that week, as the schedule's heatmap does. */
    onJumpToDate?: (d: Date) => void;
    /** Day planner → the Visit pool page, pinned to this consultant. */
    onGoPool?: (email: string, name: string) => void;
}) => {
    const { config, showPoints } = usePointsConfig();
    // Clicking a chip opens the same visit detail the schedule shows.
    const [selectedVisit, setSelectedVisit] = useState<SfVisit | null>(null);
    // Day planner: what could fill this day, from the visit pool.
    const [plannerTarget, setPlannerTarget] = useState<PlannerTarget | null>(null);
    // Admin days added here change THIS board only — same prototype behaviour as
    // the schedule, since there is no visit-write API. Keyed `${email}|${dateISO}`.
    const [dayOverrides, setDayOverrides] = useState<Record<string, string | null>>({});

    const histFrom = addDays(mondayOf(weekMonday), -7 * HISTORY_WEEKS);
    const forwardTo = addDays(mondayOf(weekMonday), 7 * FORWARD_WEEKS);
    const { data: visits, isLoading, isError } = useConsultantVisits(
        toISODate(histFrom),
        toISODate(forwardTo),
        consultant.email,
        true,
    );
    // Own query, not a slice of the history window — month view can step outside
    // it. Same key as the schedule and report, so usually a cache hit.
    const periodFrom = toISODate(dates[0]);
    const periodTo = toISODate(addDays(dates[dates.length - 1], 1));
    const periodQuery = useConsultantVisits(periodFrom, periodTo, consultant.email, true);
    const timesheets = useTimesheets(periodFrom, toISODate(dates[dates.length - 1]));
    const ownLogged = timesheets.byEmail.get((consultant.email || "").toLowerCase());
    const periodEntries = useMemo(() => [...(ownLogged?.values() ?? [])].flat(), [ownLogged]);
    const roster = useActiveConsultants();
    const sfLink = useSalesforceLink();
    const employeeId = roster.active.find((c) => (c.email || "").toLowerCase() === (consultant.email || "").toLowerCase())?.id;
    const pool = useVisitPool(toISODate(addDays(new Date(), -365)), toISODate(addDays(new Date(), 90)));

    const all = useMemo(() => visits ?? [], [visits]);
    const today = new Date();

    const toOccasion = (v: SfVisit) => ({
        accountId: v.accountId,
        accountName: v.accountName,
        date: v.visitStartDate ? toISODate(new Date(v.visitStartDate)) : "",
        subActivity: v.subActivity,
        name: v.name,
        // A booked visit with no flag is on-site work whose admin field is blank.
        visitCategory: v.visitCategory || "On Site",
        industry: v.industry,
        fraRiskLevel: v.fraRiskLevel,
        highRiskFra: v.highRiskFra,
    });

    const stats = useMemo(() => {
        const delivered = all.filter((v) => v.visitStartDate && new Date(v.visitStartDate) < today);
        const upcoming = all.filter((v) => v.visitStartDate && new Date(v.visitStartDate) >= today);

        // Points per week over the delivered history.
        const weeks: Array<{ label: string; pts: number; visits: number }> = [];
        for (let i = HISTORY_WEEKS; i >= 1; i--) {
            const wStart = addDays(mondayOf(weekMonday), -7 * i);
            const wEnd = addDays(wStart, 7);
            const inWeek = delivered.filter((v) => {
                const d = new Date(v.visitStartDate as string);
                return d >= wStart && d < wEnd;
            });
            weeks.push({
                label: wStart.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
                pts: periodPoints(inWeek.map(toOccasion), config).recognition,
                visits: inWeek.length,
            });
        }

        const owed = (pool.data ?? []).filter(
            (v) => (v.allocatedToEmail || "").toLowerCase() === (consultant.email || "").toLowerCase(),
        );

        return {
            upcoming,
            weeks,
            deliveredPts: periodPoints(delivered.map(toOccasion), config),
            upcomingPts: periodPoints(upcoming.map(toOccasion), config),
            deliveredVisits: delivered.length,
            upcomingVisits: upcoming.length,
            owed: owed.length,
            owedOverdue: owed.filter((v) => v.dueDate && new Date(v.dueDate) < today).length,
            owedNext: owed
                .map((v) => v.dueDate)
                .filter(Boolean)
                .sort()[0],
        };
    }, [all, pool.data, config, weekMonday, consultant.email]);

    // A day outside the summary's ~4-week window is "not known", never "free".
    const period = useMemo(() => {
        const loaded = !!periodQuery.data;
        const pv = periodQuery.data ?? [];
        const days = dates.map((d) => {
            const dayVisits = pv.filter((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate as string), d));
            const cell = dayCellsFor(consultant, d);
            const pts = periodPoints(dayVisits.map(toOccasion), config);
            // Local admin days count here too, or the stats above would disagree
            // with the cells below them.
            const key = `${consultant.email}|${toISODate(d)}`;
            const override = key in dayOverrides ? dayOverrides[key] : undefined;
            const comment = override !== undefined ? override : cell?.comment ?? null;
            return {
                date: d,
                visits: dayVisits.sort((a, b) => (a.visitStartDate || "").localeCompare(b.visitStartDate || "")),
                recognition: pts.recognition,
                capacity: pts.capacity,
                // A summary cell exists for every day the summary covers, so its
                // absence means the day is outside that window.
                covered: !!cell,
                leaveLabel: comment,
                // Work beats an absence marker: a day with a visit stays in the denominator.
                onLeave: !!comment && dayVisits.length === 0,
            };
        });

        const totals = periodPoints(
            pv.filter((v) => v.visitStartDate && dates.some((d) => sameDay(new Date(v.visitStartDate as string), d))).map(toOccasion),
            config,
        );
        const working = days.filter((x) => !x.onLeave);
        return {
            loaded,
            failed: periodQuery.isError,
            days,
            totals,
            visitCount: days.reduce((n, x) => n + x.visits.length, 0),
            leaveDays: days.filter((x) => x.onLeave).length,
            freeDays: working.filter((x) => x.covered && x.visits.length === 0).length,
            available: availablePoints(working.length, config),
        };
    }, [periodQuery.data, periodQuery.isError, dates, consultant, config, dayOverrides]);

    const periodUtil =
        period.available > 0 && period.loaded ? Math.round((period.totals.capacity / period.available) * 100) : null;

    /**
     * Visit mix, client mix, time breakdown and status for the SELECTED period
     * rather than the fixed 12-week history, so the four cards below follow the
     * Week / Month toggle like the board does.
     *
     * Every visit in the period counts, not just delivered ones — a future week
     * is a legitimate thing to look at. The time medians only ever count visits
     * with times recorded, so upcoming work shows a dash rather than a zero.
     */
    const periodStats = useMemo(() => {
        const pv = period.days.flatMap((d) => d.visits);

        const mix = new Map<string, number>();
        const industries = new Map<string, number>();
        const statuses = new Map<string, number>();
        pv.forEach((v) => {
            const t = VISIT_TYPE_LABEL[classify(v.subActivity)];
            mix.set(t, (mix.get(t) || 0) + 1);
            const k = v.industry || "not set";
            industries.set(k, (industries.get(k) || 0) + 1);
            const st = v.status || "—";
            statuses.set(st, (statuses.get(st) || 0) + 1);
        });

        const onSiteMins = pv.map((v) => v.attendanceMins).filter((m): m is number => m != null && m > 0);
        const travelMins = pv
            .map((v) => {
                const t0 = v.travelStartTime ? new Date(v.travelStartTime).getTime() : NaN;
                const t1 = v.travelEndTime ? new Date(v.travelEndTime).getTime() : NaN;
                const a0 = v.actualStartTime ? new Date(v.actualStartTime).getTime() : NaN;
                const a1 = v.actualEndTime ? new Date(v.actualEndTime).getTime() : NaN;
                if (![t0, t1, a0, a1].every(isFinite)) return null;
                return (t1 - t0 - (a1 - a0)) / 60000;
            })
            .filter((m): m is number => m != null && m > 0);
        const writeUp = pv
            .map((v) => (v.writeUpTimeMinutes == null ? null : Number(v.writeUpTimeMinutes)))
            .filter((m): m is number => m != null && m > 0);

        return {
            count: pv.length,
            mix: [...mix.entries()].sort((a, b) => b[1] - a[1]),
            industries: [...industries.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8),
            statusMix: [...statuses.entries()].sort((a, b) => b[1] - a[1]),
            onSite: pv.filter((v) => v.visitCategory !== "Remote").length,
            remote: pv.filter((v) => v.visitCategory === "Remote").length,
            medOnSite: median(onSiteMins),
            medTravel: median(travelMins),
            medWriteUp: median(writeUp),
            travelSample: travelMins.length,
        };
    }, [period.days]);

    // "this week" / "this month", for the four card subtitles.
    const periodWord = mode === "week" ? "this week" : "this month";

    // The days measure for the selected period, on the shared rule: admin uses a
    // day; holiday and sickness come out of the working days.
    const daysExceptionFor = (d: Date): DayException => {
        const key = `${consultant.email}|${toISODate(d)}`;
        if (key in dayOverrides) {
            const o = dayOverrides[key];
            return o ? { comment: o, status: o === "Admin" ? "A" : null } : null;
        }
        return dayExceptionFor(consultant, d);
    };
    const usage = daysUsage(period.days.map((x) => x.date), period.days.flatMap((x) => x.visits), daysExceptionFor);
    const util = period.loaded ? usage.pct : null;
    const booked = period.loaded ? usage.used : null;
    const availDays = period.loaded ? usage.available : null;

    // Points utilisation over the delivered history, on the same basis as the
    // report: capacity against what that many field days allow.
    const historyMonths = HISTORY_WEEKS / (52 / 12);
    const historyAvailable = availablePoints(fieldDaysPerMonth(config) * historyMonths, config);
    const ptsUtil = historyAvailable > 0 ? Math.round((stats.deliveredPts.capacity / historyAvailable) * 100) : null;
    const weeklyTarget = Math.round(((config.monthlyPointsTarget * 12) / 52) * 10) / 10;
    // The visit equivalent, from the grade's own monthly target rather than one
    // figure for everyone. Falls back to field days when the title is unknown.
    const monthlyVisits = monthlyVisitTarget(consultant.jobTitle) ?? fieldDaysPerMonth(config);
    const weeklyVisitTarget = Math.round(((monthlyVisits * 12) / 52) * 10) / 10;

    const nextVisit = stats.upcoming
        .slice()
        .sort((a, b) => (a.visitStartDate || "").localeCompare(b.visitStartDate || ""))[0];

    return (
        <Box sx={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Identity + headline numbers */}
            <Box sx={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "16px", alignItems: "stretch", "@media (max-width:1100px)": { gridTemplateColumns: "1fr" } }}>
                <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "18px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Box sx={{ width: "52px", height: "52px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: 700 }}>
                            {initials(consultant.name)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <Typography sx={{ fontSize: "17px", fontWeight: 700, color: T.ink2 }}>{consultant.name}</Typography>
                                <SfLink href={sfLink("Employee__c", employeeId)} label={`Open ${consultant.name} in Salesforce`} />
                            </Box>
                            <Typography sx={{ fontSize: "12px", color: T.muted }}>
                                {teamName || "No team"}
                                {consultant.jobTitle ? ` · ${consultant.jobTitle}` : ""}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt: "16px" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "4px" }}>
                            <Typography sx={{ fontSize: "12px", color: T.muted }}>Utilisation · days, {periodWord}</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2 }}>{util == null ? "—" : `${util}%`}</Typography>
                        </Box>
                        <Meter pct={util ?? 0} color={utilColour(util)} />
                    </Box>

                    {showPoints && (
                        <Box sx={{ mt: "12px" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: "4px" }}>
                                <Typography sx={{ fontSize: "12px", color: T.muted }}>Utilisation · points, {HISTORY_WEEKS} wk</Typography>
                                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.purpleDeep }}>{ptsUtil == null ? "—" : `${ptsUtil}%`}</Typography>
                            </Box>
                            <Meter pct={ptsUtil ?? 0} color={T.purple} />
                        </Box>
                    )}

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", mt: "16px" }}>
                        <Stat value={booked ?? "—"} label="Days used" tip="Days in the period with a visit, admin or training" />
                        <Stat value={availDays ?? "—"} label="Working days" tip="Days in the period, less holiday and sickness" />
                        <Stat value={consultant.fte ?? "—"} label="FTE" />
                        <Stat value={stats.owed} label="Owed, unbooked" color={stats.owed > 0 ? T.amber : undefined} tip="Visits allocated to them in the pool" />
                    </Box>

                    {stats.owedOverdue > 0 && (
                        <Typography sx={{ fontSize: "11.5px", color: T.red, fontWeight: 700, mt: "10px" }}>
                            {stats.owedOverdue} of those are past their due date
                        </Typography>
                    )}
                    {stats.owedNext && (
                        <Typography sx={{ fontSize: "11.5px", color: T.muted, mt: "4px" }}>
                            Next owed: {new Date(stats.owedNext).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </Typography>
                    )}
                </Box>

                <Card
                    fill
                    title={showPoints ? `Points a week · last ${HISTORY_WEEKS} weeks` : `Visits a week · last ${HISTORY_WEEKS} weeks`}
                    sub={
                        showPoints
                            ? `Delivered work only. Dashed line is ${fmtPoints(weeklyTarget)} — a week's share of the ${config.monthlyPointsTarget}-point month.`
                            : `Delivered work only. Dashed line is ${weeklyVisitTarget} — a week's share of the ${monthlyVisits}-visit month for this grade.`
                    }
                >
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: "28px" }}><CircularProgress size={20} /></Box>
                    ) : (
                        <>
                            <WeekTrend
                                weeks={stats.weeks}
                                target={showPoints ? weeklyTarget : weeklyVisitTarget}
                                unit={showPoints ? "points" : "visits"}
                            />
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "8px", mt: "14px" }}>
                                {showPoints ? (
                                    <>
                                        <Stat value={fmtPoints(stats.deliveredPts.recognition)} label={`Points delivered · ${HISTORY_WEEKS} wk`} color={T.purpleDeep} />
                                        <Stat value={fmtPoints(stats.deliveredPts.capacity)} label="Capacity used" />
                                        <Stat value={stats.deliveredPts.occasions} label="Occasions" tip="Days at one account — several reports at one site count once" />
                                        <Stat value={fmtPoints(stats.upcomingPts.recognition)} label={`Booked ahead · ${FORWARD_WEEKS} wk`} color={T.purple} />
                                    </>
                                ) : (
                                    <>
                                        <Stat value={stats.deliveredVisits} label={`Visits delivered · ${HISTORY_WEEKS} wk`} color={T.purpleDeep} />
                                        <Stat value={stats.upcomingVisits} label={`Booked ahead · ${FORWARD_WEEKS} wk`} color={T.purple} />
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                </Card>
            </Box>

            {/* The period selected in the top bar — week or month */}
            <Card
                title={mode === "week" ? `This week · ${periodLabel}` : `This month · ${periodLabel}`}
                sub={
                    period.failed
                        ? "Couldn't load this period's visits."
                        : `${dates.length} weekdays · follows the Week / Month toggle above`
                }
            >
                {period.failed ? (
                    <Typography sx={{ fontSize: "12.5px", color: T.red }}>
                        ⚠ This period could not be loaded, so nothing here is a zero.
                    </Typography>
                ) : !period.loaded ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: "20px" }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: "8px", mb: "14px" }}>
                            {showPoints && (
                                <Stat value={fmtPoints(period.totals.recognition)} label="Points in period" color={T.purpleDeep} />
                            )}
                            {showPoints && (
                                <Stat
                                    value={periodUtil == null ? "—" : `${periodUtil}%`}
                                    label="Utilisation · points"
                                    color={T.purple}
                                    tip={`Capacity against ${period.available} available across ${dates.length - period.leaveDays} working days`}
                                />
                            )}
                            <Stat value={period.visitCount} label="Visits" />
                            {showPoints && (
                                <Stat value={period.totals.occasions} label="Occasions" tip="Same account, same day counts once" />
                            )}
                            <Stat
                                value={period.freeDays}
                                label="Free days"
                                color={period.freeDays > 0 ? T.amber : undefined}
                                tip="Working days with no visit booked, within the summary's window"
                            />
                            <Stat value={period.leaveDays} label="Leave / admin" />
                        </Box>

                        {mode === "week" ? (
                            /* Week — the schedule's own day cell, whole. It brings
                               the timeline bar, the visit chips, the pairing
                               marker, the planned-hours bar and the day total, and
                               it shows a booking even on a day that also carries a
                               diary exception. */
                            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))`, gap: 0 }}>
                                {period.days.map((d) => (
                                    <Typography
                                        key={`h-${d.date.toISOString()}`}
                                        sx={{ fontSize: "11px", fontWeight: 700, color: T.muted, padding: "0 7px 5px" }}
                                    >
                                        {d.date.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit" })}
                                    </Typography>
                                ))}
                                {period.days.map((d) => {
                                    const key = `${consultant.email}|${toISODate(d.date)}`;
                                    return (
                                        <DayCell
                                            key={d.date.toISOString()}
                                            c={consultant}
                                            day={d.date}
                                            visits={periodQuery.data ?? []}
                                            loading={!period.loaded}
                                            detailFailed={period.failed}
                                            onVisitClick={setSelectedVisit}
                                            overrideComment={key in dayOverrides ? dayOverrides[key] : undefined}
                                            logged={ownLogged?.get(toISODate(d.date)) ?? []}
                                            onPlanDay={(existing) =>
                                                setPlannerTarget({
                                                    consultantName: consultant.name,
                                                    email: consultant.email,
                                                    day: d.date,
                                                    existing,
                                                })
                                            }
                                        />
                                    );
                                })}
                            </Box>
                        ) : (
                            /* Month — the schedule's own cell, so colours, points
                               and click-through mean the same on both boards. */
                            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${period.days.length}, minmax(0, 1fr))`, gap: "3px" }}>
                                {period.days.map((d) => (
                                    <Typography
                                        key={`h-${d.date.toISOString()}`}
                                        sx={{ fontSize: "8.5px", color: T.muted2, textAlign: "center", fontWeight: 700 }}
                                    >
                                        {d.date.getDate()}
                                    </Typography>
                                ))}
                                {period.days.map((d) => (
                                    <MonthCell
                                        key={d.date.toISOString()}
                                        c={consultant}
                                        day={d.date}
                                        visits={periodQuery.data ?? []}
                                        onJump={onJumpToDate}
                                    />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Card>

            <Card
                title={mode === "week" ? `Timesheet entries this week · ${periodLabel}` : `Timesheet entries this month · ${periodLabel}`}
                sub="Logged in the Tools app · follows the Week / Month toggle above"
            >
                {timesheets.isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: "20px" }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : timesheets.isError ? (
                    <Typography sx={{ fontSize: "12.5px", color: T.red }}>Couldn't load timesheet entries for this period.</Typography>
                ) : (
                    <TimesheetEntries entries={periodEntries} />
                )}
            </Card>

            {/* Mix + time */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                <Card fill title="Visit mix" sub={`${periodStats.count} visits ${periodWord} · ${periodLabel}`}>
                    {periodStats.mix.length === 0 ? (
                        <Typography sx={{ fontSize: "12.5px", color: T.muted }}>No visits {periodWord}.</Typography>
                    ) : (
                        periodStats.mix.map(([label, n]) => (
                            <BarRow key={label} label={label} value={n} max={periodStats.mix[0][1]} />
                        ))
                    )}
                    <Box sx={{ display: "flex", gap: "8px", mt: "12px" }}>
                        <Stat value={periodStats.onSite} label="On site" />
                        <Stat value={periodStats.remote} label="Remote" />
                    </Box>
                </Card>

                <Card fill title="Client mix" sub={`Industries visited ${periodWord} — sets the points rate`}>
                    {periodStats.industries.length === 0 ? (
                        <Typography sx={{ fontSize: "12.5px", color: T.muted }}>No visits {periodWord}.</Typography>
                    ) : (
                        periodStats.industries.map(([label, n]) => (
                            <BarRow
                                key={label}
                                label={label}
                                value={n}
                                max={periodStats.industries[0][1]}
                                color={label === "not set" ? T.amber : T.purple}
                            />
                        ))
                    )}
                </Card>

                <Card fill title="How a visit's day is spent" sub={`Median of visits with times recorded ${periodWord}`}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                        <Stat value={fmtMins(periodStats.medOnSite)} label="On site" />
                        <Stat value={fmtMins(periodStats.medTravel)} label="Travel, round trip" tip={`From ${periodStats.travelSample} visits with both travel and on-site times`} />
                        <Stat value={fmtMins(periodStats.medWriteUp)} label="Write-up" />
                    </Box>
                </Card>

                <Card fill title="Status of work" sub={`${periodWord.charAt(0).toUpperCase()}${periodWord.slice(1)} · ${periodLabel}`}>
                    {periodStats.statusMix.length === 0 ? (
                        <Typography sx={{ fontSize: "12.5px", color: T.muted }}>No visits {periodWord}.</Typography>
                    ) : (
                        periodStats.statusMix.map(([label, n]) => (
                            <BarRow
                                key={label}
                                label={label}
                                value={n}
                                max={periodStats.statusMix[0][1]}
                                color={label.toLowerCase() === "completed" ? T.green : label.toLowerCase() === "closed" ? T.muted2 : T.purple}
                            />
                        ))
                    )}
                </Card>
            </Box>

            {/* What's next */}
            <Card
                title="Booked ahead"
                sub={
                    nextVisit
                        ? `${stats.upcoming.length} visits to ${forwardTo.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} · next is ${fmtWhen(nextVisit.visitStartDate)}`
                        : `Nothing booked to ${forwardTo.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`
                }
            >
                {isLoading && <Box sx={{ display: "flex", justifyContent: "center", py: "18px" }}><CircularProgress size={20} /></Box>}
                {isError && <Typography sx={{ fontSize: "13px", color: T.muted }}>Couldn't load this consultant's visits.</Typography>}
                {!isLoading &&
                    !isError &&
                    stats.upcoming
                        .slice()
                        .sort((a, b) => (a.visitStartDate || "").localeCompare(b.visitStartDate || ""))
                        .slice(0, 12)
                        .map((v, i) => {
                            const [bg, fg] = statusColour(v.status);
                            const score = scoreVisit(
                                { subActivity: v.subActivity, name: v.name, visitCategory: v.visitCategory, industry: v.industry, fraRiskLevel: v.fraRiskLevel, highRiskFra: v.highRiskFra },
                                config,
                                { assumeOnSiteIfUnset: true },
                            );
                            return (
                                <Box key={v.id ?? i} sx={{ display: "flex", alignItems: "center", gap: "12px", padding: "9px 2px", borderTop: i === 0 ? "none" : `1px solid ${T.borderSoft}` }}>
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {v.accountName || "Unnamed account"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11.5px", color: T.muted }}>
                                            {fmtWhen(v.visitStartDate)}
                                            {v.subActivity ? ` · ${v.subActivity}` : ""}
                                        </Typography>
                                    </Box>
                                    {showPoints && (
                                        <Typography sx={{ fontSize: "12px", fontWeight: 800, color: T.purpleDeep, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                                            {score.points == null ? "—" : `${fmtPoints(score.points)} pt`}
                                        </Typography>
                                    )}
                                    {v.status ? <Pill bg={bg} fg={fg}>{v.status}</Pill> : null}
                                </Box>
                            );
                        })}
                {stats.upcoming.length > 12 && (
                    <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "8px" }}>
                        Showing the next 12 of {stats.upcoming.length}.
                    </Typography>
                )}
            </Card>

            {/* Read-only here: this page shows what happened, it doesn't edit the
                board. Saving stays on the schedule, where the local prototype
                edits live. */}
            <VisitModal
                visit={selectedVisit}
                edited={false}
                onClose={() => setSelectedVisit(null)}
                onSave={() => setSelectedVisit(null)}
            />

            <DayPlannerModal
                target={plannerTarget}
                onClose={() => setPlannerTarget(null)}
                onGoPool={onGoPool}
                onAddAdmin={() => {
                    if (!plannerTarget) return;
                    const key = `${plannerTarget.email}|${toISODate(plannerTarget.day)}`;
                    setDayOverrides((prev) => ({ ...prev, [key]: "Admin" }));
                    setPlannerTarget(null);
                }}
            />
        </Box>
    );
};

export default ConsultantDetail;
